/**
 * The admin team's overview. Every admin area with what waits in it, the areas that need
 * attention first. An area whose list could not be read says so, rather than "Nothing waiting".
 * News, such as wallets that signed in for the first time, shows in blue and is not counted as
 * work that waits.
 */

import {
  ArrowUpRight,
  Boxes,
  CircleAlert,
  CircleCheck,
  CircleDot,
  HandCoins,
  Inbox,
  LoaderCircle,
  type LucideIcon,
  Megaphone,
  MessageSquareWarning,
  ScanSearch,
  Volume2,
  Wallet,
} from "lucide-react";
import { Link } from "react-router";

import { ADMIN_AREAS, useAdminQueues } from "../../admin/queues.js";
import { IconTile, PageHeader } from "../../components/ui.js";

/** Each admin area's icon, by its address. */
const ICONS: Record<string, LucideIcon> = {
  "/admin/campaign-requests": Inbox,
  "/admin/deployments": Boxes,
  "/admin/campaigns": Megaphone,
  "/admin/reviews": ScanSearch,
  "/admin/issues": MessageSquareWarning,
  "/admin/settlements": HandCoins,
  "/admin/wallets": Wallet,
  "/admin/announcements": Volume2,
};

export function AdminHome() {
  const queues = useAdminQueues();
  const areas = ADMIN_AREAS.map((area) => {
    const found = area.waiting(queues);
    return {
      ...area,
      // Work that waits for the team, and news that only tells it something.
      waits: found?.news ? null : found,
      news: found?.news ? found : null,
      failed: queues.failed.has(area.to),
    };
  });
  const busy = areas.filter((area) => area.waits || area.failed).length;
  const unknown = areas.filter((area) => area.failed).length;

  return (
    <>
      <PageHeader eyebrow="Admin" title="Overview" documentTitle="Admin">
        {queues.loading
          ? "Checking what waits for the team…"
          : busy === 0
            ? "Nothing is waiting for the team right now."
            : `${busy} area${busy === 1 ? " needs" : "s need"} the team's attention.${
                unknown > 0
                  ? ` ${unknown} could not be checked, so open ${unknown === 1 ? "it" : "them"} to see why.`
                  : ""
              }`}
      </PageHeader>
      <ul aria-label="Admin areas" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {areas.map((area) => (
          <li key={area.to}>
            <Link
              to={area.to}
              className={`group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-[0_1px_2px_rgb(24_24_27/0.04),0_6px_24px_-12px_rgb(24_24_27/0.12)] ring-1 transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_48px_-18px_rgb(65_168_198/0.4)] sm:p-6 dark:bg-ink-900/80 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.04)] dark:hover:bg-ink-850/90 dark:hover:shadow-[inset_0_1px_0_rgb(255_255_255/0.06),0_18px_40px_-20px_rgb(0_0_0/0.9)] ${
                area.failed
                  ? "ring-red-500/40 hover:ring-red-500/60 dark:ring-red-400/30"
                  : area.waits
                    ? "ring-amber-500/40 hover:ring-amber-500/60 dark:ring-amber-400/30"
                    : area.news
                      ? "ring-brand-500/40 hover:ring-brand-500/60 dark:ring-brand-400/30"
                      : "ring-zinc-900/[0.07] hover:ring-brand-500/40 dark:ring-white/[0.08] dark:hover:ring-brand-400/40"
              }`}
            >
              {area.waits && !area.failed && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute -top-16 -right-16 size-40 rounded-full bg-amber-400/15 blur-2xl dark:bg-amber-400/[0.06]"
                />
              )}
              <span className="relative flex items-start justify-between gap-3">
                <IconTile icon={ICONS[area.to] ?? Inbox} />
                <ArrowUpRight
                  aria-hidden
                  className="size-4 text-zinc-400 transition duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-600 dark:group-hover:text-brand-400"
                />
              </span>
              <span className="relative mt-4 font-display font-semibold tracking-[-0.01em] text-zinc-950 dark:text-white">
                {area.title}
              </span>
              <span className="relative mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                {area.text}
              </span>
              <span
                className={`relative mt-auto flex items-center gap-1.5 pt-5 text-sm font-medium ${
                  area.failed
                    ? "text-red-700 dark:text-red-400"
                    : area.waits
                      ? "text-amber-700 dark:text-amber-300/90"
                      : area.news
                        ? "text-brand-700 dark:text-brand-300"
                        : "text-zinc-500 dark:text-zinc-400"
                }`}
              >
                {area.failed ? (
                  <CircleAlert aria-hidden className="size-4" />
                ) : area.waits || area.news ? (
                  <CircleDot aria-hidden className="size-4" />
                ) : queues.loading ? (
                  <LoaderCircle aria-hidden className="size-4 animate-spin" />
                ) : (
                  <CircleCheck
                    aria-hidden
                    className="size-4 text-emerald-600 dark:text-emerald-400"
                  />
                )}
                {area.failed
                  ? "Could not be checked"
                  : area.waits
                    ? area.waits.text
                    : area.news
                      ? area.news.text
                      : queues.loading
                        ? "Checking…"
                        : "Nothing waiting"}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
